import { useState, useRef, useEffect } from 'react';

interface Option {
    id: string;
    label: string;
    subLabel?: string;
}

interface SearchableSelectProps {
    options: Option[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    onSearch?: (query: string) => void;
    loading?: boolean;
    addNewAction?: {
        label: string;
        onClick: () => void;
    };
}

export default function SearchableSelect({
    options,
    value,
    onChange,
    placeholder = 'Search...',
    label,
    onSearch,
    loading = false,
    addNewAction
}: SearchableSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(opt => opt.id === value);

    // Handle clicks outside to close dropdown
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = onSearch
        ? options
        : options.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            opt.subLabel?.toLowerCase().includes(searchTerm.toLowerCase())
        );

    return (
        <div className="relative" ref={dropdownRef}>
            {label && (
                <label className="block z-50 text-sm font-medium mb-2 text-dark-text">
                    {label}
                </label>
            )}

            <div
                onClick={() => setIsOpen(!isOpen)}
                className="input cursor-pointer flex items-center justify-between min-h-[42px] py-1"
            >
                <div className="truncate">
                    {selectedOption ? (
                        <span>{selectedOption.label}</span>
                    ) : (
                        <span className="text-dark-muted">{placeholder}</span>
                    )}
                </div>
                <span className="text-dark-muted ml-2">
                    {isOpen ? '▴' : '▾'}
                </span>
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-dark-surface border border-dark-border rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="p-2 border-b border-dark-border bg-dark-bg">
                        <input
                            autoFocus
                            type="text"
                            placeholder="Type to filter..."
                            className="input bg-dark-bg border-none focus:ring-0 text-sm h-10"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                if (onSearch) onSearch(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>

                    <div className="max-h-60 overflow-y-auto custom-scrollbar">
                        {loading ? (
                            <div className="p-4 text-center text-dark-muted text-sm">
                                <span className="animate-spin inline-block mr-2">◌</span>
                                Searching...
                            </div>
                        ) : filteredOptions.length === 0 ? (
                            <div className="p-4 text-center text-dark-muted text-sm italic">
                                No results found
                            </div>
                        ) : (
                            filteredOptions.map((opt) => (
                                <div
                                    key={opt.id}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onChange(opt.id);
                                        setIsOpen(false);
                                        setSearchTerm('');
                                    }}
                                    className={`px-4 py-3 cursor-pointer text-sm transition-colors hover:bg-accent-purple/10 flex flex-col ${value === opt.id ? 'bg-accent-purple/20 text-accent-purple font-medium border-l-2 border-accent-purple pl-[14px]' : 'text-dark-text'
                                        }`}
                                >
                                    <span>{opt.label}</span>
                                    {opt.subLabel && (
                                        <span className="text-[10px] text-dark-muted mt-0.5">{opt.subLabel}</span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {addNewAction && (
                        <div className="p-2 border-t border-dark-border bg-dark-bg/50 mt-auto">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpen(false);
                                    addNewAction.onClick();
                                }}
                                className="w-full flex items-center justify-center gap-2 py-2.5 text-[10px] font-black uppercase tracking-widest text-accent-purple bg-accent-purple/5 hover:bg-accent-purple/10 border border-accent-purple/20 rounded-lg transition-all active:scale-[0.98]"
                            >
                                <span className="text-sm">+</span> {addNewAction.label}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
